import React, { useState } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import Button from '../common/Button';

const ClientIntroModal = ({ onClose }) => {
  const { isRTL } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: isRTL ? 'مرحباً بك في سَلِس' : 'Welcome to sls',
      icon: '👋',
      content: isRTL 
        ? 'نحن سعداء بانضمامك! هذه المنصة تساعدك على مراجعة والموافقة على المحتوى بسهولة.'
        : "We're excited to have you! This platform helps you review and approve content easily.",
    },
    {
      title: isRTL ? 'كيفية المراجعة' : 'How to Review',
      icon: '👀',
      content: isRTL 
        ? 'سيتم إرسال رابط مشاركة لكل تصميم جديد. افتح الرابط لمراجعة التصميم والموافقة عليه أو طلب التعديلات.'
        : 'You will receive a share link for each new design. Open the link to review the design and approve it or request changes.',
    },
    {
      title: isRTL ? 'الموافقة على التصميم' : 'Approve Designs',
      icon: '✅',
      content: isRTL 
        ? 'إذا أعجبك التصميم، اضغط زر "تعميد" للموافقة النهائية. يمكنك أيضاً إضافة تعليقات قبل الموافقة.'
        : 'If you like the design, click the "Approve" button for final approval. You can also add comments before approving.',
    },
    {
      title: isRTL ? 'طلب تعديلات' : 'Request Changes',
      icon: '✏️',
      content: isRTL 
        ? 'إذا كنت تريد تعديلات، اضغط "طلب تعديلات" واكتب ما تريد تغييره. سيقوم الفريق بإعادة التصميم.'
        : 'If you want changes, click "Request Changes" and write what you want to change. The team will redesign it.',
    },
    {
      title: isRTL ? 'الميزات الرئيسية' : 'Key Features',
      icon: '🎯',
      content: isRTL 
        ? '• معاينة التصاميم على المنصات المختلفة\n• إضافة تعليقات على التصاميم\n• متابعة جميع التصاميم في مكان واحد\n• الموافقة السريعة'
        : '• Preview designs on different platforms\n• Add comments on designs\n• Track all designs in one place\n• Quick approval',
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDone();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDone = () => {
    // Save to localStorage that user has seen the intro
    localStorage.setItem('client_intro_seen', 'true');
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('client_intro_seen', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`relative bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          <button
            onClick={handleSkip}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} text-white hover:text-gray-200 transition-colors`}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <div className="text-center">
            <span className="text-5xl block mb-2">{steps[currentStep].icon}</span>
            <h2 className="text-2xl font-bold text-white">
              {steps[currentStep].title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700 whitespace-pre-line leading-relaxed">
              {steps[currentStep].content}
            </p>
          </div>

          {/* Progress Indicators */}
          <div className="flex justify-center gap-2 mb-8">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-primary-600 w-8'
                    : index < currentStep
                    ? 'bg-primary-300 w-2'
                    : 'bg-gray-300 w-2'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="secondary"
              onClick={currentStep === 0 ? handleSkip : handlePrevious}
            >
              {currentStep === 0 
                ? (isRTL ? 'تخطي' : 'Skip')
                : (isRTL ? 'السابق' : 'Previous')
              }
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {currentStep + 1} / {steps.length}
              </span>
            </div>

            <Button
              variant="primary"
              onClick={handleNext}
            >
              {currentStep === steps.length - 1
                ? (isRTL ? 'ابدأ الآن!' : 'Get Started!')
                : (isRTL ? 'التالي' : 'Next')
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientIntroModal;


